import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, backToLobby, bindRoom, commitAction,
  sweepMyRooms, setMySlot, setWordSet, setCustomWords, startProblem, markFinished, isDeadRoom, recordWaiting,
} from '@/store/multiplayer';
import { generateWordSet, customSetsAvailable } from '@/store/aiWords';
import { wordSetLabel } from '@/engine/wordSets';
import {
  giveClueAction, guessAction, endTurnAction, clueProblem, teamWordsLeft,
} from '@/engine/actions/turnActions';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';

export { isDeadRoom };

// NO ROBOTS, deliberately - Matt (2026-08-29): "I'm not sure we need to
// bother making AI opponents... I don't think it'd be very fun with
// computers." Codenames is about reading the person giving the clue, so
// this is the one hub game that simply requires people. There is
// consequently no bot driver loop and no dropped-player takeover: if
// someone's connection blips, the table waits and the seat is theirs the
// moment they reload.

let unbindRoomFn = null;
let unwatchStatusFn = null;

export default createStore({
  state: {
    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,

    roomId: null,
    roomMeta: null,
    game: null, // the engine state, decoded
    presence: {},
    roomError: null,
    generatingWords: false,
  },

  getters: {
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,
    inLobby: (state) => state.roomMeta?.status === 'lobby',
    playing: (state) => Boolean(state.game) && state.game.meta.phase === 'playing',
    finished: (state) => Boolean(state.game) && state.game.meta.phase === 'finished',

    seats: (state) => [...(state.roomMeta?.playerSlots || [])].sort((a, b) => a.seatIndex - b.seatIndex),
    startProblem: (state) => startProblem(state.roomMeta),
    customSetsAvailable: () => customSetsAvailable(),
    wordSetKey: (state) => state.roomMeta?.wordSet || 'classic',
    wordSetName: (state) => wordSetLabel(state.roomMeta),

    me: (state, getters) => {
      if (state.game) return state.game.players.find((p) => p.id === getters.myUid) || null;
      return getters.seats.find((s) => s.uid === getters.myUid) || null;
    },
    myTeam: (state, getters) => getters.me?.team || null,
    isSpymaster: (state, getters) => getters.me?.role === 'spymaster',

    activeTeam: (state) => state.game?.turn.team || null,
    // Whose input the table is waiting on - a pure UI question, never an
    // authorisation (the no-thanks lesson).
    iGiveClue: (state, getters) => getters.playing
      && state.game.turn.phase === 'clue'
      && getters.myTeam === state.game.turn.team
      && getters.isSpymaster,
    iGuess: (state, getters) => getters.playing
      && state.game.turn.phase === 'guess'
      && getters.myTeam === state.game.turn.team
      && !getters.isSpymaster,

    wordsLeft: (state) => (state.game
      ? { red: teamWordsLeft(state.game, 'red'), blue: teamWordsLeft(state.game, 'blue') }
      : { red: 0, blue: 0 }),

    driverUid: (state) => {
      const humans = (state.game?.players || []).filter((p) => !p.isBot && state.presence[p.id]);
      if (!humans.length) return null;
      return humans.reduce((best, p) => (p.seat < best.seat ? p : best)).id;
    },
  },

  mutations: {
    setAuthReady: (s, v) => { s.authReady = v; },
    setUser: (s, v) => { s.user = v; },
    setSiteUser: (s, v) => { s.siteUser = v; s.siteUserReady = true; },
    setSiteUserReady: (s, v) => { s.siteUserReady = v; },
    setRoom: (s, { id, meta }) => { s.roomId = id; s.roomMeta = meta ?? null; },
    setRoomMeta: (s, v) => { s.roomMeta = v; },
    setGame: (s, v) => { s.game = v; },
    setPresence: (s, v) => { s.presence = v || {}; },
    setRoomError: (s, v) => { s.roomError = v; },
    setGeneratingWords: (s, v) => { s.generatingWords = v; },
  },

  actions: {
    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the game has ended - the fingerprint transaction in
    // userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state, getters }) {
      const outcome = outcomeOf({
        game: state.game,
        myTeam: getters.myTeam,
        online: Boolean(state.roomId),
      });
      if (!outcome) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ game: state.game, roomId: state.roomId }),
        outcome,
      });
    },

    initAuth({ commit }) {
      if (!firebaseConfigured || !auth) { commit('setAuthReady', true); return; }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a player.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? { uid: user.uid, displayName: user.displayName, email: user.email } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    async signIn({ commit }) {
      if (!auth) return;
      try { markInteractiveSignIn(); await signInWithPopup(auth, googleProvider); } catch (e) { commit('setRoomError', e.message); }
    },

    async signOutOfSite({ commit, dispatch }) {
      await dispatch('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    // -------------------------------------------------------------- rooms
    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    async hostRoom({ commit, state, dispatch }, { async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        const { gameId } = await createRoom({ user: state.user, async: asyncRoom });
        dispatch('enterRoom', gameId);
      } catch (e) { commit('setRoomError', e.message); }
    },

    async joinRoomByCode({ commit, state, dispatch }, code) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        const { gameId } = await joinRoom({ user: state.user, code });
        dispatch('enterRoom', gameId);
      } catch (e) { commit('setRoomError', e.message); }
    },

    enterRoom({ commit, state, dispatch }, gameId) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: gameId, meta: null });
      commit('setGame', null);

      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => { commit('setRoomMeta', meta); if (meta === null) dispatch('leaveRoom'); },
        onState: (game) => { commit('setGame', game); dispatch('maybeMarkFinished'); },
        onPresence: (presence) => commit('setPresence', presence),
      });
    },

    async leaveRoom({ commit, state }) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      if (state.roomId && state.user) {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setGame', null);
      commit('setPresence', {});
    },

    async chooseSlot({ commit, state }, { team, role }) {
      try {
        await setMySlot({ user: state.user, gameId: state.roomId, meta: state.roomMeta, team, role });
      } catch (e) { commit('setRoomError', e.message); }
    },

    async chooseWordSet({ commit, state }, key) {
      try {
        await setWordSet({ user: state.user, gameId: state.roomId, meta: state.roomMeta, key });
      } catch (e) { commit('setRoomError', e.message); }
    },

    /**
     * Turns a host's prompt into a custom set via the codenames-ai Lambda.
     * Failures surface as a notice rather than silently falling back -
     * the host is standing there waiting for their set.
     */
    async generateCustomSet({ commit, state }, prompt) {
      commit('setRoomError', null);
      commit('setGeneratingWords', true);
      try {
        const words = await generateWordSet(prompt);
        await setCustomWords({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, label: prompt, words,
        });
      } catch (e) {
        commit('setRoomError', e.message);
      } finally {
        commit('setGeneratingWords', false);
      }
    },

    async deal({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (e) { commit('setRoomError', e.message); }
    },

    async returnToLobby({ commit, state }) {
      try {
        await backToLobby({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (e) { commit('setRoomError', e.message); }
    },

    // -------------------------------------------------------------- moves
    async giveClue({ commit, state, getters }, { word, count }) {
      if (!getters.iGiveClue) return;
      const problem = clueProblem(state.game, word);
      if (problem) { commit('setRoomError', problem); return; }
      commit('setRoomError', null);
      try {
        const result = await commitAction({ gameId: state.roomId, action: giveClueAction(word, count) });
        if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
      } catch (e) { commit('setRoomError', e.message); }
    },

    async guess({ commit, state, getters, dispatch }, index) {
      if (!getters.iGuess) return;
      try {
        const result = await commitAction({ gameId: state.roomId, action: guessAction(index) });
        if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
        dispatch('maybeMarkFinished');
      } catch (e) { commit('setRoomError', e.message); }
    },

    async endTurn({ commit, state, getters }) {
      if (!getters.iGuess) return;
      try {
        const result = await commitAction({ gameId: state.roomId, action: endTurnAction() });
        if (result.committed) recordWaiting({ gameId: state.roomId, gameState: result.state, meta: state.roomMeta });
      } catch (e) { commit('setRoomError', e.message); }
    },

    maybeMarkFinished({ state, getters }) {
      if (!state.roomId || state.roomMeta?.status !== 'playing') return;
      if (state.game?.meta.phase !== 'finished') return;
      if (getters.driverUid !== state.user?.uid) return;
      markFinished({ gameId: state.roomId });
    },

  },
});
